import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { 
  CogIcon,
  CreditCardIcon,
  EnvelopeIcon,
  BuildingOfficeIcon,
  PlusIcon,
  TrashIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  SparklesIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { emailConfig, isEmailConfigured, getEmailConfigStatus, getEmailJSKeys } from '../config/emailConfig';
import { AISettings } from './AISettings';
import { UserManagement } from './UserManagement';
import { WooCommerceSettings } from './WooCommerceSettings';
import { PaymentBusinessSettings } from './PaymentBusinessSettings';
import { InvoiceGroupsSettings } from './InvoiceGroupsSettings';
import { apiService } from '../services/apiService';
import { useEffect } from 'react';
import { toast } from 'sonner';

interface Company {
  id: string;
  name: string;
  email: string;
  cif: string;
  address: string;
  isActive: boolean;
  createdAt: Date;
}

interface SettingsProps {
  onClose: () => void;
  variant?: 'modal' | 'inline';
  // Users/Companies management
  users?: any[];
  companies?: any[];
  currentUser?: any;
  currentCompany?: any;
  onCreateUser?: (userData: any) => void;
  onCreateCompany?: (companyData: any) => void;
  onUpdateUser?: (userId: string, userData: any) => void;
  onUpdateCompany?: (companyId: string, companyData: any) => void;
  onDeleteUser?: (userId: string) => void;
  onDeleteCompany?: (companyId: string) => void;
  onSwitchCompany?: (companyId: string) => void;
  onOpenAISettings?: () => void;
  onOpenAutoBilling?: () => void;
  onExportBackup?: () => void;
  onImportBackup?: (file: File) => void;
  initialSection?: string;
  wcOauthState?: string | null;
  wcOauthStore?: string | null;
}

const ConfigurationStatus: React.FC = () => {
  const status = getEmailConfigStatus();
  
  return (
    <div className="text-xs space-y-2">
      <div className={`${status.isReady ? 'text-green-700' : 'text-yellow-700'}`}>
        <p className="font-medium mb-2">Estado de configuración:</p>
        <div className="space-y-1 ml-2">
          <p><span className="font-medium">Public Key:</span> {status.publicKey}</p>
          <p><span className="font-medium">Service ID:</span> {status.serviceId}</p>
          <p><span className="font-medium">Template ID:</span> {status.templateId}</p>
        </div>
      </div>
      
      {status.isReady ? (
        <div className="text-green-700 bg-green-50 p-2 rounded">
          <p>🎉 <span className="font-medium">¡Listo para probar!</span></p>
          <p>Puedes enviar emails de prueba ahora.</p>
          <p className="text-xs mt-1">El Template ID es opcional para las pruebas básicas.</p>
        </div>
      ) : (
        <div className="text-yellow-700">
          <p>⚠️ Faltan configuraciones. Revisa los elementos marcados como "❌ Pendiente"</p>
        </div>
      )}
    </div>
  );
};

export const Settings: React.FC<SettingsProps> = ({
  onClose,
  users,
  companies: companiesProp,
  currentUser: currentUserProp,
  currentCompany: currentCompanyProp,
  onOpenAutoBilling,
  initialSection = 'email',
  wcOauthState = null,
  wcOauthStore = null,
}) => {
  const [activeSection, setActiveSection] = useState(initialSection);

  useEffect(() => {
    if (wcOauthState) setActiveSection('woocommerce');
  }, [wcOauthState]);
  const [testEmail, setTestEmail] = useState('test@ejemplo.com');
  const [emailStatus, setEmailStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [emailMessage, setEmailMessage] = useState('');
  const [verifactuStatus, setVerifactuStatus] = useState<{
    loading: boolean;
    message: string;
    data?: Record<string, unknown>;
  }>({ loading: false, message: '' });

  // Gmail SMTP config (stored in localStorage)
  const [gmailConfig, setGmailConfig] = useState(() => {
    const saved = localStorage.getItem('gmailConfig');
    return saved ? JSON.parse(saved) : { user: '', appPassword: '', fromName: 'FactuApp' };
  });
  
  const [companies, setCompanies] = useState<Company[]>([
    {
      id: '1',
      name: 'Daniel Vázquez Facturación',
      email: 'daniel@facturacion.com',
      cif: '12345678A',
      address: 'Calle Principal 123, Madrid',
      isActive: true,
      createdAt: new Date()
    }
  ]);

  const [showNewCompanyForm, setShowNewCompanyForm] = useState(false);
  const [newCompany, setNewCompany] = useState({
    name: '',
    email: '',
    cif: '',
    address: ''
  });
  
  // Configuración de empresa para facturas
  const [companyInfo, setCompanyInfo] = useState(() => {
    const saved = localStorage.getItem('companyInfo');
    return saved ? JSON.parse(saved) : {
      name: 'Daniel Vázquez Facturación',
      description: 'Servicios profesionales de desarrollo web',
      address: 'Calle Principal 123, Madrid',
      email: 'daniel@facturacion.com',
      phone: '+34 600 000 000',
      footerMessage: 'Gracias por confiar en nuestros servicios',
      footerSubMessage: 'Para cualquier consulta, no dude en contactarnos'
    };
  });

  // Configuración de facturación por empresa
  const [invoiceConfig, setInvoiceConfig] = useState(() => {
    const saved = localStorage.getItem('invoiceConfig');
    return saved ? JSON.parse(saved) : {
      invoicePrefix: 'FAC',
      currentYear: new Date().getFullYear(),
      lastInvoiceNumber: 0,
      autoResetYear: true,
      fiscalYearStart: 1 // Enero
    };
  });

  const [emailjsForm, setEmailjsForm] = useState(() => {
    const k = getEmailJSKeys();
    return {
      publicKey: k.publicKey || '',
      serviceId: k.serviceId || '',
      templateId: k.templateId || ''
    };
  });

  const handleSaveCompanyInfo = () => {
    // Aquí se guardaría en localStorage o backend
    localStorage.setItem('companyInfo', JSON.stringify(companyInfo));
    toast.success('Información de empresa guardada correctamente');
  };

  const handleSaveInvoiceConfig = () => {
    localStorage.setItem('invoiceConfig', JSON.stringify(invoiceConfig));
    toast.success('Configuración de facturación guardada correctamente');
  };

  const handleExportBackup = () => {
    const backupData = {
      version: '1.0.0',
      timestamp: new Date().toISOString(),
      companyInfo,
      invoiceConfig,
      appVersion: '1.0.0'
    };
    
    const dataStr = JSON.stringify(backupData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    
    const link = document.createElement('a');
    link.href = URL.createObjectURL(dataBlob);
    link.download = `backup-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
  };

  const handleImportBackup = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const backupData = JSON.parse(e.target?.result as string);
        
        if (backupData.companyInfo) {
          setCompanyInfo(backupData.companyInfo);
          localStorage.setItem('companyInfo', JSON.stringify(backupData.companyInfo));
        }
        
        if (backupData.invoiceConfig) {
          setInvoiceConfig(backupData.invoiceConfig);
          localStorage.setItem('invoiceConfig', JSON.stringify(backupData.invoiceConfig));
        }
        
        toast.success('Backup restaurado correctamente');
      } catch (error) {
        toast.error('Error al restaurar el backup: ' + error);
      }
    };
    reader.readAsText(file);
  };

  const handleTestEmail = async () => {
    setEmailStatus('sending');
    setEmailMessage('');

    try {
      // Si hay Gmail configurado, probar backend SMTP
      if (gmailConfig.user && gmailConfig.appPassword) {
        const { apiService } = await import('../services/apiService');
        const api = apiService;
        const resp = await api.testGmailSMTP({
          user: gmailConfig.user,
          appPassword: gmailConfig.appPassword,
          fromName: gmailConfig.fromName,
          toEmail: testEmail || gmailConfig.user
        });
        if (resp.error) {
          setEmailStatus('error');
          setEmailMessage(`Error: ${resp.message || resp.error}`);
          return;
        }
        setEmailStatus('success');
        setEmailMessage('Email de prueba enviado correctamente (Gmail SMTP)');
        return;
      }
    
    // Verificar si EmailJS está configurado
    if (!isEmailConfigured()) {
      setEmailStatus('error');
        setEmailMessage('Configura Gmail (usuario y contraseña de app) o EmailJS en src/config/emailConfig.ts');
      return;
    }
    
      // Inicializar EmailJS
      const keys = getEmailJSKeys();
      if (!keys.publicKey || !keys.serviceId) {
        setEmailStatus('error');
        setEmailMessage('Faltan claves de EmailJS: Public Key y Service ID');
        return;
      }
      emailjs.init(keys.publicKey);
      
      // Preparar datos para el template
      const templateParams = {
        to_email: testEmail,
        from_name: emailConfig.testEmailTemplate.from_name,
        from_email: emailConfig.testEmailTemplate.from_email,
        subject: emailConfig.testEmailTemplate.subject,
        // Aliases para plantillas existentes
        name: emailConfig.testEmailTemplate.from_name,
        nombre: emailConfig.testEmailTemplate.from_name,
        email: emailConfig.testEmailTemplate.from_email,
        title: emailConfig.testEmailTemplate.subject,
        message: emailConfig.testEmailTemplate.message.replace(
          '{{current_datetime}}', 
          new Date().toLocaleString('es-ES')
        ),
        mensaje: emailConfig.testEmailTemplate.message.replace(
          '{{current_datetime}}', 
          new Date().toLocaleString('es-ES')
        ),
        reply_to: testEmail
      };
      
      let response;
      
      // Si no tenemos Template ID, crear un template simple por defecto
      if (!keys.templateId || keys.templateId === 'TU_TEMPLATE_ID_AQUI') {
        // Usar un template genérico de EmailJS para pruebas
        const simpleParams = {
          to_email: testEmail,
          from_name: 'Credens Clothing',
          from_email: 'info@credensclothing.com',
          subject: 'Test de configuración - FactuApp',
          message: `¡Hola!\n\nEste es un email de prueba del sistema de facturación.\n\nFecha: ${new Date().toLocaleString('es-ES')}\n\nSaludos,\nCredens Clothing`,
          reply_to: 'info@credensclothing.com'
        };
        
        // Intentar con un template ID genérico común de EmailJS
        try {
          response = await emailjs.send(
            keys.serviceId,
            'template_test', // ID genérico común
            simpleParams
          );
        } catch (error) {
          // Si falla, informar que necesita crear un template
          throw new Error('Necesitas crear un Template en EmailJS. Ve a Email Templates > Create New Template.');
        }
      } else {
        // Enviar email usando template configurado
        response = await emailjs.send(
          keys.serviceId,
          keys.templateId,
          templateParams
        );
      }
      
      if (response.status === 200) {
        setEmailStatus('success');
        setEmailMessage(`✅ Email de prueba enviado correctamente a ${testEmail}`);
      } else {
        setEmailStatus('error');
        setEmailMessage(`❌ Error al enviar: ${response.text || 'Error desconocido'}`);
      }
      
    } catch (error: any) {
      setEmailStatus('error');
      
      // Manejar diferentes tipos de errores
      if (error.text) {
        // Error de EmailJS
        if (error.text.includes('Invalid email')) {
          setEmailMessage('❌ Dirección de email inválida');
        } else if (error.text.includes('service')) {
          setEmailMessage('❌ Error de configuración del servicio. Verifica tu Service ID');
        } else if (error.text.includes('template')) {
          setEmailMessage('❌ Error de template. Verifica tu Template ID');
        } else if (error.text.includes('public key')) {
          setEmailMessage('❌ Public Key inválido. Verifica tu configuración');
        } else {
          setEmailMessage(`❌ Error de EmailJS: ${error.text}`);
        }
      } else if (error.message) {
        setEmailMessage(`❌ Error: ${error.message}`);
      } else {
        setEmailMessage('❌ Error desconocido al enviar el email');
      }
      
      console.error('Error enviando email:', error);
    }
  };

  const handleAddCompany = () => {
    if (!newCompany.name || !newCompany.email || !newCompany.cif) {
      toast.error('Por favor completa todos los campos obligatorios');
      return;
    }

    const company: Company = {
      id: `company_${Date.now()}`,
      ...newCompany,
      isActive: false,
      createdAt: new Date()
    };

    setCompanies(prev => [...prev, company]);
    setNewCompany({ name: '', email: '', cif: '', address: '' });
    setShowNewCompanyForm(false);
  };

  const handleDeleteCompany = (companyId: string) => {
    if (companies.find(c => c.id === companyId)?.isActive) {
      toast.error('No puedes eliminar la empresa activa. Cambia a otra empresa primero.');
      return;
    }
    
    if (window.confirm('¿Estás seguro de que quieres eliminar esta empresa?')) {
      setCompanies(prev => prev.filter(c => c.id !== companyId));
    }
  };

  const handleActivateCompany = (companyId: string) => {
    setCompanies(prev => prev.map(c => ({
      ...c,
      isActive: c.id === companyId
    })));
  };

  // Estado mínimo para pasar a UserManagement inline
  const [inlineUsers, setInlineUsers] = useState<any[]>(users || []);
  const [inlineCompanies, setInlineCompanies] = useState<any[]>(companiesProp || []);
  const [inlineCurrentUser, setInlineCurrentUser] = useState<any>(currentUserProp || null);
  const [inlineCurrentCompany, setInlineCurrentCompany] = useState<any>(currentCompanyProp || null);
  const [manageError, setManageError] = useState<string>('');

  const reloadManageData = async () => {
    try {
      setManageError('');
      const prof = await apiService.getProfile();
      if (!(prof as any).error && (prof.data as any)?.user) {
        setInlineCurrentUser((prof.data as any).user);
      }
      const com = await apiService.getCompanies();
      if ((com as any).error) {
        setManageError((com as any).error || 'No se pudieron cargar las empresas');
      } else {
        const data = (com.data as any);
        const norm = (data.companies || []).map((c: any) => ({
          ...c,
          isActive: c.isActive !== undefined ? !!c.isActive : (c.is_active !== undefined ? !!c.is_active : true),
          postalCode: c.postalCode ?? c.postal_code,
        }));
        setInlineCompanies(norm);
        const cc = data.currentCompany ? ({
          ...data.currentCompany,
          isActive: data.currentCompany.isActive !== undefined ? !!data.currentCompany.isActive : (data.currentCompany.is_active !== undefined ? !!data.currentCompany.is_active : true),
          postalCode: data.currentCompany.postalCode ?? data.currentCompany.postal_code,
        }) : (norm[0] || null);
        setInlineCurrentCompany(cc);
      }
      const usr = await apiService.getUsers();
      if ((usr as any).error) {
        setManageError((usr as any).error || manageError || 'No se pudieron cargar los usuarios');
      } else {
        setInlineUsers((usr.data as any)?.users || []);
      }
    } catch (e: any) {
      setManageError('Error cargando datos. Inicia sesión e inténtalo de nuevo.');
    }
  };

  useEffect(() => {
    if (activeSection === 'manage') {
      reloadManageData();
    }
    if (activeSection === 'verifactu') {
      checkVerifactuIntegration();
    }
  }, [activeSection]);

  const checkVerifactuIntegration = async () => {
    setVerifactuStatus({ loading: true, message: '' });
    try {
      const response = await apiService.getVerifactuHealth();
      if ((response as { error?: string }).error) {
        setVerifactuStatus({
          loading: false,
          message:
            (response as { message?: string }).message ||
            (response as { error?: string }).error ||
            'No se pudo comprobar VeriFactu'
        });
        return;
      }
      setVerifactuStatus({
        loading: false,
        message: 'Estado VeriFactu actualizado',
        data: (response as { data?: { verifactu?: Record<string, unknown> } }).data?.verifactu
      });
    } catch (e: unknown) {
      setVerifactuStatus({
        loading: false,
        message: e instanceof Error ? e.message : 'Error comprobando VeriFactu'
      });
    }
  };

  const isPrivileged =
    currentUserProp?.role === 'admin' || currentUserProp?.role === 'superadmin';

  const sections = [
    { id: 'verifactu', name: 'VeriFactu (AEAT)', icon: BuildingOfficeIcon },
    { id: 'payment', name: 'Formas de pago', icon: CreditCardIcon },
    { id: 'business', name: 'Líneas de negocio', icon: BuildingOfficeIcon },
    { id: 'invoice-groups', name: 'Grupos de facturas', icon: BuildingOfficeIcon },
    { id: 'email', name: 'Test de Email', icon: EnvelopeIcon },
    { id: 'ia', name: 'Configuración de IA', icon: SparklesIcon },
    { id: 'auto', name: 'Facturación Automática', icon: CalendarDaysIcon },
    { id: 'backup', name: 'Backup y Restauración', icon: ArrowDownTrayIcon },
    ...(isPrivileged ? [{ id: 'woocommerce', name: 'WooCommerce', icon: DocumentTextIcon }] : []),
    ...(isPrivileged ? [{ id: 'manage', name: 'Usuarios y Empresas', icon: BuildingOfficeIcon }] : [])
  ];

  return (
    <div className="fixed inset-0 bg-black/30 dark:bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-start justify-center p-4 sm:pt-16">
      <div className="relative w-full max-w-4xl bg-white dark:bg-[#161b22] border border-[#E6E9F0] dark:border-[#21262d] rounded-2xl shadow-stripe-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E6E9F0] dark:border-[#21262d]">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#635BFF]/10 flex items-center justify-center">
              <CogIcon className="h-4.5 w-4.5 h-[18px] w-[18px] text-[#635BFF]" />
            </div>
            <h2 className="text-base font-semibold text-[#0A2540] dark:text-[#e6edf3]">Configuración</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-[#8898aa] hover:text-[#425466] hover:bg-[#F6F9FC] dark:hover:bg-[#21262d] transition-colors"
          >
            <span className="sr-only">Cerrar</span>
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex flex-col sm:flex-row">
          {/* Sidebar */}
          <div className="w-full sm:w-56 border-b sm:border-b-0 sm:border-r border-[#E6E9F0] dark:border-[#21262d] p-3">
            <nav className="space-y-0.5">
              {sections.map((section) => {
                const Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      activeSection === section.id
                        ? 'bg-[#EEF0FF] text-[#635BFF] dark:bg-[#635BFF]/15 dark:text-[#a5a9ff]'
                        : 'text-[#425466] dark:text-[#8b949e] hover:bg-[#F6F9FC] dark:hover:bg-[#21262d] hover:text-[#0A2540] dark:hover:text-[#e6edf3]'
                    }`}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    {section.name}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content */}
          <div className="flex-1 p-6 min-h-0 overflow-y-auto max-h-[70vh]">
            {activeSection === 'verifactu' && (
              <div className="space-y-5">
                <div>
                  <h3 className="text-base font-semibold text-[#0A2540] dark:text-[#e6edf3]">VeriFactu — AEAT</h3>
                  <p className="text-sm text-[#425466] dark:text-[#8b949e] mt-1">
                    Huella SHA-256 encadenada, QR de cotejo y remisión voluntaria SOAP según la{' '}
                    <a
                      href="https://sede.agenciatributaria.gob.es/Sede/iva/sistemas-informaticos-facturacion-verifactu/informacion-tecnica.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#635BFF] hover:text-[#4F46E5] underline"
                    >
                      documentación técnica AEAT
                    </a>
                    .
                  </p>
                </div>

                <div className="rounded-lg border border-amber-200 dark:border-amber-800/40 bg-amber-50 dark:bg-amber-900/10 p-4 text-sm text-amber-800 dark:text-amber-300">
                  <p className="font-semibold">Certificado y entorno</p>
                  <p className="mt-1">
                    La remisión a Hacienda exige un <strong>certificado electrónico cualificado</strong> instalado en el servidor
                    (<code className="bg-amber-100 dark:bg-amber-900/30 px-1 rounded">VERIFACTU_CERT_PFX_PATH</code>). Pruebas: entorno <code className="bg-amber-100 dark:bg-amber-900/30 px-1 rounded">pruebas</code> y portal{' '}
                    <a href="https://preportal.aeat.es/" target="_blank" rel="noopener noreferrer" className="underline">
                      preportal.aeat.es
                    </a>
                    .
                  </p>
                </div>

                <div className="rounded-xl border border-[#E6E9F0] dark:border-[#21262d] bg-[#F6F9FC] dark:bg-[#0d1117] p-4 space-y-3">
                  <button
                    type="button"
                    onClick={checkVerifactuIntegration}
                    disabled={verifactuStatus.loading}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-[#635BFF] text-white px-4 py-2 text-sm font-semibold hover:bg-[#4F46E5] disabled:opacity-60 transition-colors shadow-sm"
                  >
                    {verifactuStatus.loading ? 'Comprobando...' : 'Comprobar estado VeriFactu'}
                  </button>
                  {verifactuStatus.message ? (
                    <p className="text-sm text-gray-700">{verifactuStatus.message}</p>
                  ) : null}
                  {verifactuStatus.data ? (
                    <div className="text-sm bg-gray-50 border rounded p-3 space-y-1">
                      <p>
                        <strong>Entorno:</strong> {String(verifactuStatus.data.environment)}
                      </p>
                      <p>
                        <strong>Remisión lista:</strong>{' '}
                        {verifactuStatus.data.remisionReady ? 'Sí' : 'No (falta certificado)'}
                      </p>
                      <p>
                        <strong>Endpoint SOAP:</strong>{' '}
                        {verifactuStatus.data.soapEndpointConfigured ? 'Configurado' : 'Por defecto según entorno'}
                      </p>
                      <p>
                        <strong>Certificado:</strong>{' '}
                        {verifactuStatus.data.certConfigured ? 'Sí' : 'No'}
                      </p>
                      <p>
                        <strong>Cadena local:</strong>{' '}
                        {verifactuStatus.data.freeLocalChain ? 'Activa' : 'No'}
                      </p>
                    </div>
                  ) : null}
                  <p className="text-xs text-[#8898aa] border-t pt-3">
                    En cada factura: <strong>Generar VeriFactu</strong> (huella + QR), luego <strong>Remitir a AEAT</strong> si el certificado está configurado.
                  </p>
                </div>
              </div>
            )}

            {activeSection === 'payment' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-base font-semibold text-[#0A2540] dark:text-[#e6edf3]">Formas de pago</h3>
                  <p className="text-sm text-[#425466] dark:text-[#8b949e] mt-1">Transferencia (IBAN/concepto), Bizum, tarjeta y defaults por origen (Reservas → tarjeta).</p>
                </div>
                <PaymentBusinessSettings initialTab="payment" />
              </div>
            )}

            {activeSection === 'business' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-base font-semibold text-[#0A2540] dark:text-[#e6edf3]">Líneas de negocio / Categorías</h3>
                  <p className="text-sm text-[#425466] dark:text-[#8b949e] mt-1">Entik Media, Conciertos, Entik Records — pie PDF y pago por defecto por proyecto.</p>
                </div>
                <PaymentBusinessSettings initialTab="business" />
              </div>
            )}

            {activeSection === 'invoice-groups' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-base font-semibold text-[#0A2540] dark:text-[#e6edf3]">Grupos de facturas</h3>
                  <p className="text-sm text-[#425466] dark:text-[#8b949e] mt-1">
                    Etiquetas para filtrar y organizar facturas. Puede asignar varios grupos a cada factura.
                  </p>
                </div>
                <InvoiceGroupsSettings />
              </div>
            )}

            {activeSection === 'email' && (
              <div className="space-y-6">
                {/* EmailJS keys */}
                <div className="rounded-xl border border-[#E6E9F0] dark:border-[#21262d] bg-[#F6F9FC] dark:bg-[#0d1117] p-4">
                  <div className="mb-3 font-medium">EmailJS</div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                      <label className="block text-sm text-[#425466] dark:text-[#8b949e] mb-1">Public Key</label>
                      <input className="w-full border border-[#E6E9F0] dark:border-[#30363d] rounded-lg px-3 py-2 bg-white dark:bg-[#0d1117] text-[#0A2540] dark:text-[#e6edf3] focus:outline-none focus:border-[#635BFF] transition-colors text-sm" value={emailjsForm.publicKey} onChange={e=>setEmailjsForm({...emailjsForm, publicKey:e.target.value})} placeholder="user_xxx"/>
                    </div>
                    <div>
                      <label className="block text-sm text-[#425466] dark:text-[#8b949e] mb-1">Service ID</label>
                      <input className="w-full border border-[#E6E9F0] dark:border-[#30363d] rounded-lg px-3 py-2 bg-white dark:bg-[#0d1117] text-[#0A2540] dark:text-[#e6edf3] focus:outline-none focus:border-[#635BFF] transition-colors text-sm" value={emailjsForm.serviceId} onChange={e=>setEmailjsForm({...emailjsForm, serviceId:e.target.value})} placeholder="service_xxx"/>
                    </div>
                    <div>
                      <label className="block text-sm text-[#425466] dark:text-[#8b949e] mb-1">Template ID (opcional)</label>
                      <input className="w-full border border-[#E6E9F0] dark:border-[#30363d] rounded-lg px-3 py-2 bg-white dark:bg-[#0d1117] text-[#0A2540] dark:text-[#e6edf3] focus:outline-none focus:border-[#635BFF] transition-colors text-sm" value={emailjsForm.templateId} onChange={e=>setEmailjsForm({...emailjsForm, templateId:e.target.value})} placeholder="template_xxx"/>
                    </div>
                  </div>
                  <div className="mt-3">
                    <button className="inline-flex items-center rounded-lg bg-[#635BFF] text-white px-4 py-2 text-sm font-semibold hover:bg-[#4F46E5] transition-colors shadow-sm" onClick={()=>{localStorage.setItem('emailjsConfig', JSON.stringify(emailjsForm)); toast.success('EmailJS guardado');}}>
                      Guardar EmailJS
                    </button>
                  </div>
                </div>

                {/* Gmail SMTP config */}
                <div className="rounded-xl border border-[#E6E9F0] dark:border-[#21262d] bg-[#F6F9FC] dark:bg-[#0d1117] p-4">
                  <div className="flex items-center mb-3">
                    <EnvelopeIcon className="h-5 w-5 text-gray-600 mr-2" />
                    <span className="font-medium">Correo (Gmail SMTP)</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-[#425466] dark:text-[#8b949e] mb-1">Usuario de Gmail</label>
                    <input
                      type="email"
                        className="w-full border border-[#E6E9F0] dark:border-[#30363d] rounded-lg px-3 py-2 bg-white dark:bg-[#0d1117] text-[#0A2540] dark:text-[#e6edf3] focus:outline-none focus:border-[#635BFF] transition-colors text-sm"
                        placeholder="tuusuario@gmail.com"
                        value={gmailConfig.user}
                        onChange={(e) => setGmailConfig({ ...gmailConfig, user: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-[#425466] dark:text-[#8b949e] mb-1">Contraseña de App (Gmail)</label>
                      <input
                        type="password"
                        className="w-full border border-[#E6E9F0] dark:border-[#30363d] rounded-lg px-3 py-2 bg-white dark:bg-[#0d1117] text-[#0A2540] dark:text-[#e6edf3] focus:outline-none focus:border-[#635BFF] transition-colors text-sm"
                        placeholder="16 caracteres de contraseña de app"
                        value={gmailConfig.appPassword}
                        onChange={(e) => setGmailConfig({ ...gmailConfig, appPassword: e.target.value })}
                      />
                      <p className="text-xs text-[#8898aa] mt-1">Genera una contraseña de app en tu cuenta Google: Seguridad → Contraseñas de aplicaciones</p>
                    </div>
                    <div>
                      <label className="block text-sm text-[#425466] dark:text-[#8b949e] mb-1">Nombre del remitente</label>
                      <input
                        type="text"
                        className="w-full border border-[#E6E9F0] dark:border-[#30363d] rounded-lg px-3 py-2 bg-white dark:bg-[#0d1117] text-[#0A2540] dark:text-[#e6edf3] focus:outline-none focus:border-[#635BFF] transition-colors text-sm"
                        placeholder="FactuApp"
                        value={gmailConfig.fromName}
                        onChange={(e) => setGmailConfig({ ...gmailConfig, fromName: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-[#425466] dark:text-[#8b949e] mb-1">Email de prueba (destino)</label>
                      <input
                        type="email"
                        className="w-full border border-[#E6E9F0] dark:border-[#30363d] rounded-lg px-3 py-2 bg-white dark:bg-[#0d1117] text-[#0A2540] dark:text-[#e6edf3] focus:outline-none focus:border-[#635BFF] transition-colors text-sm"
                        placeholder="destinatario@correo.com"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                    />
                  </div>
                  </div>
                  <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2 mt-3">
                  <button
                      className="inline-flex items-center rounded-lg bg-[#635BFF] text-white px-4 py-2 text-sm font-semibold hover:bg-[#4F46E5] transition-colors shadow-sm"
                      onClick={() => { localStorage.setItem('gmailConfig', JSON.stringify(gmailConfig)); toast.success('Configuración de Gmail guardada'); }}
                    >
                      Guardar Gmail
                    </button>
                    <button
                      className="inline-flex items-center rounded-lg bg-emerald-600 text-white px-4 py-2 text-sm font-semibold hover:bg-emerald-700 transition-colors shadow-sm"
                    onClick={handleTestEmail}
                    >
                        Enviar Email de Prueba
                  </button>
                    {emailStatus === 'sending' && <span className="text-sm text-[#8898aa]">Enviando...</span>}
                    {emailStatus === 'success' && <span className="text-sm text-green-600">{emailMessage}</span>}
                    {emailStatus === 'error' && <span className="text-sm text-red-600">{emailMessage}</span>}
                      </div>
                </div>

                {/* Estado de configuración */}
                <div className={`p-4 rounded-lg border ${
                  isEmailConfigured() 
                    ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/10 dark:border-emerald-800/40' 
                    : 'bg-amber-50 border-amber-200 dark:bg-amber-900/10 dark:border-amber-800/40'
                }`}>
                  <div className="flex items-center mb-2">
                    {isEmailConfigured() ? (
                      <CheckCircleIcon className="h-5 w-5 text-emerald-600 mr-2" />
                    ) : (
                      <InformationCircleIcon className="h-5 w-5 text-amber-600 mr-2" />
                    )}
                    <h4 className={`text-sm font-medium ${
                      isEmailConfigured() ? 'text-emerald-800 dark:text-emerald-300' : 'text-amber-800 dark:text-amber-300'
                    }`}>
                      Estado de configuración
                    </h4>
                  </div>
                  
                  <ConfigurationStatus />
                </div>


              </div>
            )}

            {activeSection === 'ia' && (
              <div className="space-y-6">
                <AISettings onClose={() => setActiveSection('email')} />
              </div>
            )}

            {activeSection === 'auto' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-base font-semibold text-[#0A2540] dark:text-[#e6edf3]">Facturación Automática</h3>
                  <p className="text-sm text-[#425466] dark:text-[#8b949e] mt-1">Gestiona tus suscripciones y cobros recurrentes.</p>
                </div>
                <div className="rounded-xl border border-[#E6E9F0] dark:border-[#21262d] bg-[#F6F9FC] dark:bg-[#0d1117] p-4">
                  <p className="text-sm text-gray-700 mb-3">Abre el gestor de facturación automática para crear, editar o desactivar cargos recurrentes.</p>
                  <button
                    onClick={() => {
                      onClose();
                      if (typeof onOpenAutoBilling === 'function') {
                        onOpenAutoBilling();
                      }
                    }}
                    className="inline-flex items-center rounded-lg bg-[#635BFF] text-white px-4 py-2 text-sm font-semibold hover:bg-[#4F46E5] transition-colors shadow-sm"
                  >
                    Abrir gestor
                  </button>
                </div>
              </div>
            )}

            {activeSection === 'backup' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-base font-semibold text-[#0A2540] dark:text-[#e6edf3]">Backup y Restauración</h3>
                  <p className="text-sm text-[#425466] dark:text-[#8b949e] mt-1">
                    Gestiona copias de seguridad y restauración de datos del sistema.
                  </p>
                </div>

                <div className="bg-purple-50 p-6 rounded-lg">
                  <div className="flex items-center mb-4">
                    <ArrowDownTrayIcon className="h-6 w-6 text-purple-600 mr-2" />
                    <h4 className="text-lg font-medium text-purple-900">Exportar Backup</h4>
                  </div>
                  
                  <p className="text-sm text-gray-700 mb-4">
                    Crea una copia de seguridad completa de todos los datos del sistema incluyendo empresas, usuarios, facturas y clientes.
                  </p>
                  
                  <button
                    onClick={handleExportBackup}
                    className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition-colors flex items-center space-x-2"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                    <span>Exportar Backup</span>
                  </button>
                </div>

                <div className="bg-green-50 p-6 rounded-lg">
                  <div className="flex items-center mb-4">
                    <ArrowUpTrayIcon className="h-6 w-6 text-emerald-600 mr-2" />
                    <h4 className="text-lg font-medium text-emerald-800 dark:text-emerald-300">Restaurar Backup</h4>
                  </div>
                  
                  <p className="text-sm text-gray-700 mb-4">
                    Restaura todos los datos desde un archivo de backup. <strong>¡Atención!</strong> Esto sobrescribirá todos los datos actuales.
                  </p>
                  
                  <div className="flex items-center space-x-4">
                    <input
                      type="file"
                      accept=".json"
                      onChange={handleImportBackup}
                      className="block w-full text-sm text-[#8898aa] file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                    />
                  </div>
                  
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <div className="flex">
                      <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">Advertencia</h3>
                        <div className="mt-2 text-sm text-yellow-700">
                          <p>La restauración de backup reemplazará todos los datos actuales. Asegúrate de hacer una copia de seguridad antes de proceder.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeSection === 'woocommerce' && isPrivileged && (
              <WooCommerceSettings
                currentUser={currentUserProp}
                oauthState={wcOauthState}
                oauthStore={wcOauthStore}
              />
            )}

            {activeSection === 'manage' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-[#425466] dark:text-[#8b949e]">
                      <span>Usuarios: {inlineUsers.length}</span>
                      <span className="ml-3">Empresas: {inlineCompanies.length}</span>
                    </div>
                    <button className="text-sm px-3 py-1.5 rounded-md border hover:bg-gray-50" onClick={reloadManageData}>
                      Recargar
                    </button>
                  </div>
                  {manageError && (
                    <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{manageError}</div>
                  )}
                <UserManagement
                  users={inlineUsers as any}
                  companies={inlineCompanies as any}
                  currentUser={inlineCurrentUser as any}
                  currentCompany={inlineCurrentCompany as any}
                  onClose={onClose}
                  onCreateUser={async (u)=>{
                    await apiService.createUser(u as any);
                    const usr = await apiService.getUsers();
                    if (!('error' in usr)) setInlineUsers((usr.data as any)?.users || []);
                  }}
                  onUpdateUser={async (id, u)=>{
                    await apiService.updateUser(id, u as any);
                    const usr = await apiService.getUsers();
                    if (!('error' in usr)) setInlineUsers((usr.data as any)?.users || []);
                  }}
                  onDeleteUser={async (id)=>{
                    await apiService.deleteUser(id);
                    const usr = await apiService.getUsers();
                    if (!('error' in usr)) setInlineUsers((usr.data as any)?.users || []);
                  }}
                  onSwitchCompany={async (id)=>{
                    await apiService.activateCompany(id);
                    const com = await apiService.getCompanies();
                    if (!('error' in com)) {
                      const data = (com.data as any);
                      const norm = (data.companies || []).map((c: any) => ({
                        ...c,
                        isActive: c.isActive !== undefined ? !!c.isActive : (c.is_active !== undefined ? !!c.is_active : true),
                        postalCode: c.postalCode ?? c.postal_code,
                      }));
                      setInlineCompanies(norm);
                      const cc = data.currentCompany ? ({
                        ...data.currentCompany,
                        isActive: data.currentCompany.isActive !== undefined ? !!data.currentCompany.isActive : (data.currentCompany.is_active !== undefined ? !!data.currentCompany.is_active : true),
                        postalCode: data.currentCompany.postalCode ?? data.currentCompany.postal_code,
                      }) : (norm[0] || null);
                      setInlineCurrentCompany(cc);
                    }
                  }}
                  onCreateCompany={async (c)=>{
                    await apiService.createCompany(c as any);
                    const com = await apiService.getCompanies();
                    if (!('error' in com)) {
                      const data = (com.data as any);
                      const norm = (data.companies || []).map((x: any) => ({
                        ...x,
                        isActive: x.isActive !== undefined ? !!x.isActive : (x.is_active !== undefined ? !!x.is_active : true),
                        postalCode: x.postalCode ?? x.postal_code,
                      }));
                      setInlineCompanies(norm);
                    }
                  }}
                  onUpdateCompany={async (id, c)=>{
                    await apiService.updateCompany(id, c as any);
                    const com = await apiService.getCompanies();
                    if (!('error' in com)) {
                      const data = (com.data as any);
                      const norm = (data.companies || []).map((x: any) => ({
                        ...x,
                        isActive: x.isActive !== undefined ? !!x.isActive : (x.is_active !== undefined ? !!x.is_active : true),
                        postalCode: x.postalCode ?? x.postal_code,
                      }));
                      setInlineCompanies(norm);
                    }
                  }}
                  onDeleteCompany={async (id)=>{
                    await apiService.deleteCompany(id);
                    const com = await apiService.getCompanies();
                    if (!('error' in com)) {
                      const data = (com.data as any);
                      const norm = (data.companies || []).map((x: any) => ({
                        ...x,
                        isActive: x.isActive !== undefined ? !!x.isActive : (x.is_active !== undefined ? !!x.is_active : true),
                        postalCode: x.postalCode ?? x.postal_code,
                      }));
                      setInlineCompanies(norm);
                    }
                  }}
                  onOpenAISettings={()=>setActiveSection('ia')}
                  onExportBackup={()=>{}}
                  onImportBackup={()=>{}}
                  variant="inline"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}; 